// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Search, ShoppingCart, User, Tag, Star, Package, LogIn, LogOut, ChevronDown } from 'lucide-react';

export default function NavBar() {
  const { user, customer, signOut } = useAuth();
  const { itemCount, setIsOpen }    = useCart();
  const navigate = useNavigate();
  const [search, setSearch]         = useState('');
  const [userMenu, setUserMenu]     = useState(false);

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/products?q=${encodeURIComponent(search.trim())}`);
  };

  const handleSignOut = async () => {
    await signOut();
    setUserMenu(false);
    navigate('/');
  };

  const tier = customer?.grocery_loyalty_tiers;

  return (
    <nav className="navbar">
      <div className="navbar-inner">
        {/* Brand */}
        <Link to="/" className="navbar-brand" style={{ textDecoration: 'none' }}>
          🛒 <span>Main Street Grocery</span>
        </Link>

        {/* Search */}
        <form className="navbar-search" onSubmit={handleSearch}>
          <Search size={16} className="navbar-search-icon" />
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </form>

        {/* Nav links */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8 }}>
          <Link to="/coupons" style={{ textDecoration: 'none' }}>
            <button className="btn btn-ghost btn-sm" style={{ gap: 4, color: '#16A34A', fontWeight: 600 }}>
              <Tag size={16} />
              <span style={{ display: 'none', fontSize: 13 }} className="nav-label">Coupons</span>
            </button>
          </Link>
          <Link to="/loyalty" style={{ textDecoration: 'none' }}>
            <button className="btn btn-ghost btn-sm" style={{ gap: 4, color: '#D97706', fontWeight: 600 }}>
              <Star size={16} />
              {tier && (
                <span style={{ display: 'none', fontSize: 12, fontWeight: 700 }} className="nav-label">
                  {tier.tier_icon} {customer?.loyalty_points?.toLocaleString()} pts
                </span>
              )}
            </button>
          </Link>
        </div>

        <div className="navbar-actions">
          {/* Cart button */}
          <button
            className="btn btn-ghost btn-icon"
            style={{ position: 'relative' }}
            onClick={() => setIsOpen(true)}
          >
            <ShoppingCart size={22} color="#16A34A" />
            {itemCount > 0 && (
              <span style={{
                position: 'absolute', top: -6, right: -6,
                background: '#EF4444', color: '#fff',
                width: 20, height: 20, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 800,
              }}>
                {itemCount > 99 ? '99+' : itemCount}
              </span>
            )}
          </button>

          {/* User menu */}
          {user ? (
            <div style={{ position: 'relative' }}>
              <button
                className="btn btn-ghost btn-sm"
                style={{ gap: 6, color: '#14532D', fontWeight: 600 }}
                onClick={() => setUserMenu(!userMenu)}
              >
                <div style={{
                  width: 30, height: 30, borderRadius: '50%',
                  background: '#16A34A', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700,
                }}>
                  {(customer?.full_name || user.email)[0].toUpperCase()}
                </div>
                <ChevronDown size={14} />
              </button>
              {userMenu && (
                <div style={{
                  position: 'absolute', top: '110%', right: 0, zIndex: 200,
                  background: '#fff', borderRadius: 12, padding: '8px 0',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
                  border: '1px solid #BBF7D0', minWidth: 200,
                }}>
                  <div style={{ padding: '8px 16px 10px', borderBottom: '1px solid #F0FDF4' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#14532D' }}>
                      {customer?.full_name || user.email.split('@')[0]}
                    </div>
                    <div style={{ fontSize: 11, color: '#94A3B8' }}>{user.email}</div>
                    {tier && (
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#D97706', marginTop: 4 }}>
                        {tier.tier_icon} {tier.tier_name} · {customer?.loyalty_points?.toLocaleString()} pts
                      </div>
                    )}
                  </div>
                  {[
                    { to: '/account', icon: User,    label: 'My Account' },
                    { to: '/orders',  icon: Package, label: 'My Orders' },
                    { to: '/loyalty', icon: Star,    label: 'Loyalty Rewards' },
                  ].map(item => (
                    <Link key={item.to} to={item.to} style={{ textDecoration: 'none' }} onClick={() => setUserMenu(false)}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '9px 16px', cursor: 'pointer',
                        color: '#14532D', fontSize: 14, fontWeight: 500,
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = '#F0FDF4'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <item.icon size={15} />
                        {item.label}
                      </div>
                    </Link>
                  ))}
                  <div style={{ borderTop: '1px solid #F0FDF4', marginTop: 4 }}>
                    <button
                      onClick={handleSignOut}
                      style={{
                        width: '100%', padding: '9px 16px',
                        display: 'flex', alignItems: 'center', gap: 10,
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#EF4444', fontSize: 14, fontWeight: 500,
                      }}
                    >
                      <LogOut size={15} />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" style={{ textDecoration: 'none' }}>
              <button className="btn btn-primary btn-sm">
                <LogIn size={16} />
                Sign In
              </button>
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
}
